<script setup lang="ts">
import { computed, ref } from 'vue';
import Button from '@ui/components/forms/button.vue';
import Icon from '@ui/components/icon.vue';
import DropzoneArea from '@ui/components/forms/dropzoneArea/DropzoneArea.vue';
import SmartFormFields from '@app/components/forms/smartFormFields.vue';
import HelpScreenshotButton from '@app/components/help/HelpScreenshotButton.vue';
import { useHelp } from './useHelp';

const {
    DROPZONE_NAMESPACE,
    MAX_ATTACHMENTS,
    MAX_SINGLE_ATTACHMENT_BYTES,
    MAX_TOTAL_ATTACHMENT_BYTES,
    smartFormRef,
    formFields,
    showFormErrors,
    validationMessages,
    dropzoneError,
    submittingTicket,
    handleFieldsUpdate,
    cancelSupportForm,
    submitSupportForm,
    handleDropzoneFilesAdded,
    handleFilesRejected,
    handleDropzoneError,
    handleFileAction,
    attachments,
    addAttachmentFromCapture,
    helpData,
} = useHelp();

const texts = computed(() => helpData.value.texts);

const screenshotError = ref<string | null>(null);

const attachmentsLimitReached = computed(
    () => attachments.value.length >= MAX_ATTACHMENTS,
);

const handleScreenshotCapture = (payload: { file: File; base64: string }) => {
    addAttachmentFromCapture(payload);
    screenshotError.value = null;
};

const handleScreenshotError = (payload: { message: string }) => {
    screenshotError.value = payload.message;
};
</script>

<template>
    <section class="flex flex-1 flex-col overflow-hidden bg-white">
        <DropzoneArea
            :namespace="DROPZONE_NAMESPACE"
            :max-files="MAX_ATTACHMENTS"
            :max-file-size="MAX_SINGLE_ATTACHMENT_BYTES"
            :max-total-size="MAX_TOTAL_ATTACHMENT_BYTES"
            :allow-click-to-pick="false"
            :allow-paste="true"
            overlay
            :ui="{ root: 'flex flex-1 relative overflow-hidden' }"
            @files-added="handleDropzoneFilesAdded"
            @files-rejected="handleFilesRejected"
            @error="handleDropzoneError"
        >
            <template #overlay>
                <div class="absolute inset-0 flex items-center justify-center bg-white/50 backdrop-blur">
                    <Icon
                        icon="far fa-paperclip"
                        size="xlarge"
                        class="text-brand-500 mb-4 animate-bounce"
                    ></Icon>
                </div>
            </template>
            <div class="flex flex-1 flex-col overflow-hidden">
                <div class="customScrollbar flex grow flex-col gap-4 overflow-y-auto p-4 pt-3">
                    <SmartFormFields
                        ref="smartFormRef"
                        :fields="formFields"
                        :show-errors="showFormErrors"
                        :validation-messages="validationMessages"
                        :validate-on-load="true"
                        allow-changes
                        controlled
                        wrapper-class="flex flex-col gap-4"
                        class="flex flex-col gap-4"
                        :disabled="submittingTicket"
                        @on-file="handleFileAction"
                        @update:fields="handleFieldsUpdate"
                    >
                        <template #customFields="slotProps">
                            <div
                                v-if="slotProps.filter?.name === 'attachments'"
                                class="mt-2 flex flex-col gap-2"
                            >
                                <HelpScreenshotButton
                                    :disabled="submittingTicket || attachmentsLimitReached"
                                    @capture="handleScreenshotCapture"
                                    @error="handleScreenshotError"
                                ></HelpScreenshotButton>
                                <div
                                    v-if="screenshotError"
                                    class="text-sm text-red-600"
                                >
                                    {{ screenshotError }}
                                </div>
                            </div>
                        </template>
                    </SmartFormFields>
                    <p
                        v-if="dropzoneError"
                        class="text-sm text-red-600"
                    >
                        {{ dropzoneError }}
                    </p>
                </div>
                <div class="mt-auto flex shrink-0 items-center justify-end gap-4 border-t border-gray-200 bg-gray-50 px-4 py-3">
                    <Button
                        :disabled="submittingTicket"
                        class="grow"

                        @click="cancelSupportForm"
                    >
                        {{ texts.supportForm.actions.cancel }}
                    </Button>
                    <Button
                        theme="primary"
                        :disabled="submittingTicket"
                        class="shrink-0 grow"
                        :icon="submittingTicket ? 'far fa-spinner-third' : 'far fa-paper-plane'"
                        @click="submitSupportForm"
                    >
                        {{ texts.supportForm.actions.submit }}
                    </Button>
                </div>
            </div>
        </DropzoneArea>
    </section>
</template>
